import React, { useContext, useEffect, useState } from 'react';
import Dialog from 'src/components/common/Dialog';
import Button from 'src/components/controls/forms/redesign/FormButton';
// import moment from 'moment';
import RHFInputBox from 'src/components/controls/tempRHFControls/RHFInputBox';
import RHFSelect from 'src/components/controls/tempRHFControls/RHFSelect';
import { useForm } from 'react-hook-form';
import { AppContext } from 'src/providers/AppContextProvider';
import { IAppContextState } from 'src/reducers/AppContext';
import useFetch from 'use-http';
import moment from 'moment';
import PersonnelToast from '../../components/PersonnelToast';
import LandmanPayInfo from 'src/models/personnel/LandmanPayInfo';
import TSDILandman from 'src/models/personnel/TSDILandman';

interface IUpdatePayHistoryDialogProps {
    dialogName: string;
    currentRate: number;
    currentUnit: string;
    selectedPersonnel: TSDILandman;
    showToast: (message: string, color?: string, persistent?: boolean) => void;
    updateScreenPay: () => void;
    hideDialog: () => void;
}

const UpdatePayHistoryDialog = (props: IUpdatePayHistoryDialogProps) => {
    const { get, post, response } = useFetch();
    const { register, errors, getValues, setValue, handleSubmit, reset } = useForm();
    const [toast, setToast] = useState<{ message: string; color?: string, persistent?: boolean; } | null>(null);
    const [oldPeriodNo, setOldPeriodNo] = useState<string>('');

    const appContext = useContext<IAppContextState>(AppContext);
    const currentUser = {
        name: appContext.currentUser.username,
        id: appContext.currentUser.userId,
        lmsNumber: appContext.currentUser.lmsNumber
    };

    const effectivePeriodNoRegex = /^\d{4}-\d{2}$/;

    useEffect(() => {
        onCreate();
    }, []);

    useEffect(() => {
        if (props.currentRate !== null && props.currentRate !== undefined) {
            setValue('currentRate', props.currentRate);
        } else {
            setValue('currentRate', 0);
        }
    }, [props.currentRate]);

    useEffect(() => {
        if (props.currentUnit !== null && props.currentUnit !== undefined) {
            switch (props.currentUnit) {
                case 'D':
                    setValue('currentPayUnit', 'D');
                    break;
                case 'H':
                    setValue('currentPayUnit', 'H');
                    break;
                case 'M':
                    setValue('currentPayUnit', 'M');
                    break;
                default:
                    setValue('currentPayUnit', 'U');
            }
        } else {
            setValue('currentPayUnit', 'U');
        }
    }, [props.currentUnit]);

    useEffect(() => {
        reset({
            currentRate: props.currentRate ?? 0,
            currentPayUnit: props.currentUnit ?? 'U'
        });
    }, [props.currentRate, props.currentUnit]);

    const onCreate = () => {
        console.log('UpdatePayHistoryDialog onCreate()');
    };

    const showToast = (message: string, color?: string, persistent?: boolean) => {
        setToast({ message, persistent, color });
    };

    // gets period start date from period number
    const updateEffectivePeriodDate = async () => {
        let newPeriodNo = getValues('effPeriodNo');
        if (!effectivePeriodNoRegex.test(newPeriodNo)) {
            if (newPeriodNo.lengh >= 7) {
                showToast('Ensure new period has the proper format: YYYY-MM', `rgba(255,0,0,0.5)`);
            }

            return null;
        }

        if (newPeriodNo === oldPeriodNo) {
            return null;
        }

        let formattedNo = parseInt(newPeriodNo.replace('-', ''), 10);

        let result = await get(`/v1.0/landmen/personnel/${formattedNo}/tax-period-date`);
        if (response.ok) {
            let formattedDate = result ? (moment(result).format('YYYY-MM-DD') as unknown as Date) : null;
            setValue('effDate', formattedDate);
            setOldPeriodNo(newPeriodNo);
            return result;
        } else {
            showToast('Unable to update effective date: ' + result.message, `rgba(255,0,0,0.5)`);
            return null;
        }
    };

    // gets manager name by entered employee number
    const getByLMSNumber = async () => {
        let lmsNumber = getValues(`mgrNo`);
        if (lmsNumber.length >= 4) {
            let result = await get(`/v1.0/landmen/personnel/${lmsNumber}/get-name-by-lms`);
            if (response.ok) {
                if (result !== '') {
                    setValue(`tsdUser`, result);
                    setValue(`mgrNo`, lmsNumber);
                } else {
                    showToast('Unable to find valid name with provided lms number: ' + lmsNumber, `rgba(255,0,0,0.5)`);
                }
            } else {
                showToast('Unable to find user with provided lms number: ' + lmsNumber, `rgba(255,0,0,0.5)`);
            }
        }
    };

    const submitData = async (data: LandmanPayInfo) => {
        let finalData = new LandmanPayInfo();

        finalData.lmNo = props.selectedPersonnel?.lmNo;
        finalData.payUnit = data.payUnit;
        finalData.payRate = parseFloat(data.payRate.toString());
        finalData.chgDate = moment(new Date()).format('YYYY-MM-DD') as unknown as Date;
        finalData.mgrNo = parseFloat(data.mgrNo.toString());
        finalData.prevUnit = props.currentUnit;
        finalData.prevRate = props.currentRate;
        finalData.tsdUser = data.tsdUser.toLocaleUpperCase();
        finalData.effDate = data.effDate;

        let result = await post(`v1.0/landmen/personnel/${props.selectedPersonnel.lmNo}/update-pay-history`, finalData);

        if (response.ok) {
            props.showToast('Default contract pay successfully updated');
            props.updateScreenPay();
            props.hideDialog();
        } else {
            showToast('Unable to update pay history: ' + result.message, `rgba(255,0,0,0.5)`);
        }
    };

    return (
        <Dialog title="Update Contract Pay Rate" dialogName={props.dialogName} isExitButtonInvisible={true} bodyStyle={{ width: 'auto' }}>
            <div style={{ position: 'relative' }}>
                <div>
                    {toast && (
                        <PersonnelToast
                            message={toast.message}
                            color={toast.color}
                            onClose={() => setToast(null)}
                            persistent={toast.persistent}
                        />
                    )}
                </div>
                <h3>Current Pay Rate</h3>
                <div className="mb-2">
                    <form onSubmit={handleSubmit(submitData)}>
                        <RHFSelect
                            name={'currentPayUnit'}
                            label={'Pay By'}
                            labelOnLeft={true}
                            register={register}
                            errors={errors}
                            isRequired={true}
                            disabled={true}
                            spaceLabelContent={true}
                            options={[
                                { value: 'D', label: 'Day' },
                                { value: 'H', label: 'Hour' },
                                { value: 'M', label: 'Month' },
                                { value: 'U', label: 'Unknown' }
                            ]}
                        />
                        <RHFInputBox
                            name={'currentRate'}
                            label={'Pay Rate'}
                            labelOnLeft={true}
                            register={register}
                            errors={errors}
                            isRequired={true}
                            disabled={true}
                            spaceLabelContent={true}
                        />
                        <h3>New Pay Rate</h3>
                        <RHFSelect
                            name={'payUnit'}
                            label={'Pay By'}
                            labelOnLeft={true}
                            register={register}
                            spaceLabelContent={true}
                            errors={errors}
                            isRequired={true}
                            defaultVal={'Day'}
                            options={[
                                { value: 'D', label: 'Day' },
                                { value: 'H', label: 'Hour' },
                                { value: 'M', label: 'Month' },
                            ]}
                        />
                        <RHFInputBox
                            name={'payRate'}
                            label={'Pay Rate'}
                            labelOnLeft={true}
                            spaceLabelContent={true}
                            register={register}
                            errors={errors}
                            isRequired={true}
                        />
                        <RHFInputBox
                            name={'effPeriodNo'}
                            label={'Eff Period No.'}
                            labelOnLeft={true}
                            spaceLabelContent={true}
                            register={register}
                            errors={errors}
                            isRequired={true}
                            onChange={() => updateEffectivePeriodDate()}
                        />
                        <RHFInputBox
                            name={'effDate'}
                            label={'Eff Date.'}
                            type="date"
                            labelOnLeft={true}
                            spaceLabelContent={true}
                            register={register}
                            errors={errors}
                            disabled={true}
                        />
                        <div className="form-row">
                            <RHFInputBox
                                type="text"
                                label={'Manager / HR'}
                                labelOnLeft={true}
                                spaceLabelContent={true}
                                register={register}
                                name="mgrNo"
                                style={{ width: '25%', display: 'block' }}
                                defaultVal={currentUser.lmsNumber?.toString()}
                                onChange={() => getByLMSNumber()}
                            />
                            <RHFInputBox
                                type="text"
                                register={register}
                                name="tsdUser"
                                style={{ width: '100%', display: 'block' }}
                                defaultVal={currentUser.name}
                            />
                        </div>
                    </form>
                </div>
                <div className="button-row" style={{ paddingBottom: '1rem' }}>
                    <Button text="Update" onClick={handleSubmit(submitData)} intent="primary" />
                    <Button text="Cancel" onClick={() => props.hideDialog()} intent="secondary" />
                </div>
            </div>
        </Dialog>
    );
};

export default UpdatePayHistoryDialog;



// WEBPACK FOOTER //
// ./src/components/personnel/dialogs/pay/UpdateDefaultContractPayDialog.tsx